import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import ReactDatePicker from 'react-datepicker'
import { FocusScope } from 'radix-ui/internal'

import { DATE_FORMATS } from '@/utils/dateHelpers'
import { QUARTER_PNL_OPTIONS, YEARLY_PNL_OPTIONS } from '@/constants'
import {
  DeviceWidth,
  DeviceWidthCombo,
  useIsDeviceWidth,
} from '@/utils/deviceWidthHelpers'

import { mtc } from '../../../utils/style-helpers'
import { DatePickerInput } from './input'
import { DatePickerHeader } from './header'
import { Select } from '../../atoms/select'
import { Button } from '../../atoms/button'
import { Label } from '../../atoms/label'

import {
  convertSelectedDateToString,
  parseDateStringToDate,
  createDisplayValue,
  createSelectedDate,
  createDateValue,
  findSelectedOptionIndex,
  createPopoverModifiers,
} from './helpers'
import { MobileDatePicker } from './MobilePicker'
import 'react-datepicker/dist/react-datepicker.css'
import {
  datePickerStyles,
  leftPanelStyles,
  footerStyles,
  cancelButtonStyles,
  calendarSectionStyles,
  monthContainerStyles,
} from './styles'

type RangeProps =
  | {
      selectsRange?: false
      onChange: (value: string) => void
      value?: string
    }
  | {
      selectsRange: true
      onChange: (value: [string, string]) => void
      value?: [string, string]
    }

interface BaseProps {
  dateFormat?: string
  className?: string
  startDate?: string
  endDate?: string
  showMonthYearPicker?: boolean
  showYearPicker?: boolean
  [key: string]: unknown // Allow additional props
}

export type IDatePickerProps = BaseProps & RangeProps

export const DatePicker = ({
  dateFormat = DATE_FORMATS.INPUT,
  onChange,
  value,
  className,
  startDate,
  endDate,
  selectsRange,
  showMonthYearPicker,
  showYearPicker,
  ...rest
}: IDatePickerProps) => {
  const pickerRef = useRef<ReactDatePicker>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const portalId = useId()
  const callerPortalId =
    typeof rest.portalId === 'string' ? rest.portalId : undefined
  const [portalReady, setPortalReady] = useState(false)
  const fallbackRange = useMemo(
    () => [parseDateStringToDate(startDate), parseDateStringToDate(endDate)],
    [startDate, endDate]
  )
  const isMobileOrTablet = useIsDeviceWidth(DeviceWidthCombo.mobileOrTablet)
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const [openModal, _setOpenModal] = useState(false)
  const [showYear, setShowYear] = useState(Boolean(showYearPicker))

  // Tells the popover to stay within the content boundaries and give it a little extra padding
  const popoverModifiers = useMemo(() => createPopoverModifiers(), [])

  // Portaled to body so no clipping ancestor (scroll areas, dialogs) can cut the calendar.
  // Radix layers treat body-level pointer and focus events as outside interactions, so those
  // are stopped at the host. Body is pointer-events: none under a modal layer.
  useEffect(() => {
    const portalTarget = document.createElement('div')
    portalTarget.id = portalId
    portalTarget.className = 'pointer-events-auto relative z-3000'
    const stopOutsideDetection = (event: Event) => event.stopPropagation()
    portalTarget.addEventListener('pointerdown', stopOutsideDetection)
    portalTarget.addEventListener('focusin', stopOutsideDetection)
    document.body.appendChild(portalTarget)
    setPortalReady(true)

    return () => {
      portalTarget.remove()
      setPortalReady(false)
    }
  }, [portalId])

  useEffect(() => {
    if (isMobile) {
      pickerRef.current?.setOpen(openModal)
    }
  }, [isMobile, openModal])

  const [[start, end], setRange] = useState(fallbackRange)

  // This is not a perfect representation of the value sent back to parent - it's for the date picker selection so
  // doesn't have to be perfect
  const selectedDate = useMemo(
    () =>
      createSelectedDate(
        value as string | [string, string] | undefined,
        dateFormat
      ),
    [dateFormat, value]
  )

  // This is not a perfect representation of the value sent back to parent - it's for the date picker selection so
  // doesn't have to be perfect
  const dateValue = useMemo(
    () =>
      createDateValue(
        value as string | [string, string] | undefined,
        dateFormat
      ),
    [dateFormat, value]
  )

  // Formatted value for display in the input
  const displayValue = useMemo(
    () =>
      createDisplayValue(
        value as string | [string, string] | undefined,
        selectsRange,
        start,
        end,
        dateFormat
      ),
    [value, selectsRange, start, end, dateFormat]
  )

  const selectedYearlyOption = useMemo(
    () => findSelectedOptionIndex(YEARLY_PNL_OPTIONS, startDate, endDate),
    [endDate, startDate]
  )

  const selectedQuarterOption = useMemo(
    () => findSelectedOptionIndex(QUARTER_PNL_OPTIONS, startDate, endDate),
    [endDate, startDate]
  )

  const handleClear = () => {
    if (selectsRange) {
      onChange(['', ''])
      setRange([undefined, undefined])
    } else {
      onChange('')
    }
  }

  const handleQuickOption = ({
    startDate: start,
    endDate: end,
  }: {
    startDate: string
    endDate: string
  }) => {
    setRange([parseDateStringToDate(start), parseDateStringToDate(end)])
    if (selectsRange) {
      onChange([start, end])
    }
    pickerRef.current?.setOpen(false)
  }

  // The portaled popper is no longer next in tab order, so hand focus to it from the input.
  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (typeof rest.onKeyDown === 'function') rest.onKeyDown(event)
    if (event.key !== 'Tab' || event.shiftKey) return
    const tabLoopStart = document
      .getElementById(callerPortalId ?? portalId)
      ?.querySelector<HTMLElement>('.react-datepicker__tab-loop__start')
    if (tabLoopStart) {
      event.preventDefault()
      tabLoopStart.focus()
    }
  }

  // Mounting a nested scope pauses an enclosing dialog's focus trap, which would otherwise
  // pull focus back out of the body-portaled calendar. Identity must stay stable or the
  // calendar remounts on every parent render and loses its displayed month. react-datepicker
  // also calls this while closed with no children, which must not leave a scope mounted.
  const popperContainer = useCallback(
    ({ children }: { children?: React.ReactNode }) =>
      children ? (
        <FocusScope.FocusScope
          onMountAutoFocus={(event) => event.preventDefault()}
          onUnmountAutoFocus={(event) => {
            event.preventDefault()
            if (document.activeElement === document.body) {
              wrapperRef.current?.querySelector('input')?.focus()
            }
          }}
        >
          {children}
        </FocusScope.FocusScope>
      ) : null,
    []
  )

  const handleCancel = () => {
    setRange(fallbackRange)
    pickerRef.current?.setOpen(false)
  }

  const rangeProps = selectsRange
    ? ({
        selectsRange: true as const,
        onChange: (dates: [Date | null, Date | null] | Date | null) => {
          if (Array.isArray(dates)) {
            setRange([dates[0] || undefined, dates[1] || undefined])
          }
        },
      } as const)
    : {
        onChange: (date: Date | null) => {
          if (date) {
            const formattedDate = convertSelectedDateToString(date, dateFormat)
            onChange(formattedDate)
          }
        },
      }

  if (isMobile && selectsRange) {
    return (
      <MobileDatePicker
        onChange={onChange as (value: [string, string]) => void}
        dateFormat={dateFormat}
        className={className}
        selectsRange
        showMonthYearPicker={showMonthYearPicker}
        showYearPicker={showYearPicker}
        onClearClick={handleClear}
        handleQuickOption={handleQuickOption}
        popperModifiers={popoverModifiers}
        isMobile={isMobile}
        {...rest}
      />
    )
  }

  return (
    <div
      ref={wrapperRef}
      className={`${className} ${isMobileOrTablet ? 'tablet-date-picker' : ''}`}
    >
      <style>
        {`
          div.react-datepicker__header {
            background-color: white;
            border: none;
          }
          .react-datepicker__navigation--previous{
            left: 245px;
          }
          h2.react-datepicker__current-month {
            padding: 1rem;
          }
          div.react-datepicker-popper{
            bg-white;
          }
          .react-datepicker__week {
            display: flex;
            justify-content: space-between;
            padding-bottom: 0.25rem;
          }
          div.react-datepicker__day {
            margin: 0;
            display: flex;
            justify-content: center;
          }
          div.react-datepicker__day--in-range {
            display: flex;
            background: #F0EEEA;
            border-radius: 0;
            color: #4F4F4F;
          }
          div.react-datepicker__day--range-start {
            background: #18181B;
            border-radius: 0.25rem;
            color: white;
          }
          div.react-datepicker__day--range-end {
            background: #18181B;
            border-radius: 0.25rem;
            color: white;
          }
          div.react-datepicker-popper[data-placement^=bottom] .react-datepicker__triangle {
          fill: white;
          stroke: white;
          color: white;
          }
          div.react-datepicker__day-name {
            width: 2rem;
          }
          div.react-datepicker__day--in-range:not([aria-disabled=true]):hover {
            background: #216752;
             border-radius: 0.25rem;
            color: white;
          }
          div.react-datepicker__day--in-selecting-range:not(.react-datepicker__day--in-range, .react-datepicker__month-text--in-range, .react-datepicker__quarter-text--in-range, .react-datepicker__year-text--in-range) {
            background: #F0EEEA;
            border-radius: 0.25rem;
            color: #4F4F4F;
          }
          div.react-datepicker__day--in-selecting-range:not([aria-disabled=true]):hover {
            background: #18181B;
            border-radius: 0;
            border-radius: 0.25rem;
            color: white;
          }
          
          /* Month picker styles */
          .react-datepicker__month-wrapper {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 2px;
            padding: 2px;
            max-width: 400px;
            margin: 0 auto;
          }
          
          .react-datepicker__month-text {
            padding: 10px 20px;
            margin: 0;
            background: white;
            border: 1px solid #E5E5E5;
            border-radius: 8px;
            color: #18181B;
            font-weight: 500;
            font-size: 14px;
            cursor: pointer;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 40px;
          }
          
          .react-datepicker__month-text:hover {
            background: #F5F5F5;
            border-color: #D4D4D4;
          }
          
          .react-datepicker__month-text--selected,
          .react-datepicker__month-text--keyboard-selected {
            background: #18181B;
            color: white;
            border-color: #18181B;
          }
          
          .react-datepicker__month-text--selected:hover,
          .react-datepicker__month-text--keyboard-selected:hover {
            background: #27272A;
            border-color: #27272A;
          }
            
          
          .react-datepicker__month-text--disabled {
            color: #A1A1AA;
            cursor: not-allowed;
            background: #FAFAFA;
          }
          
          .react-datepicker__month-text--disabled:hover {
            background: #FAFAFA;
            border-color: #E5E5E5;
          }
          
          /* Custom header styles for month picker */
          .react-datepicker__month-container {
            display: flex;
            flex-direction: column;
            margin-right: 1em;
          }
          .react-datepicker__header--custom {
            justify-content: space-between;
            padding: 8px 12px;
            background: white;
            border-bottom: 1px solid #E5E5E5;
          }
          
          .react-datepicker__header--custom p {
            font-size: 16px;
            font-weight: 600;
            color: #18181B;
            margin: 0;
          }
          
          .react-datepicker__header--custom svg {
            width: 16px;
            height: 16px;
          }
          
          /* Year container for month picker */
          .react-datepicker__year-container {
            padding: 0;
          }
          
          .react-datepicker__year {
            margin: 0;
          }
        `}
      </style>

      <DatePickerInput
        {...(({ value: _, ...restWithoutValue }) => restWithoutValue)(rest)}
        value={displayValue}
        dateFormat={dateFormat}
        onClearClick={handleClear}
        onClick={() => pickerRef.current?.setOpen(true)}
        onKeyDown={handleInputKeyDown}
        onChange={() => {}}
        selectsRange={selectsRange}
      />

      <ReactDatePicker
        {...(() => {
          // Remove selectsRange from rest props to avoid conflicts
          const { selectsRange: _, ...restWithoutSelectsRange } =
            rest as Record<string, unknown>
          return restWithoutSelectsRange
        })()}
        {...rangeProps}
        dateFormat={dateFormat}
        disabledKeyboardNavigation
        endDate={end}
        focusSelectedMonth
        monthsShown={selectsRange && !isMobileOrTablet ? 2 : 1}
        portalId={callerPortalId ?? (portalReady ? portalId : undefined)}
        ref={pickerRef}
        selected={selectedDate}
        shouldCloseOnSelect={
          (!selectsRange && !showYear) || Boolean(showYearPicker)
        }
        startDate={start}
        value={dateValue}
        useWeekdaysShort
        customInput={
          // The popper anchors to this element's bounding rect — `display:
          // none` collapses that to a detached zero-rect, which is what was
          // throwing the calendar's position off. Kept invisible but given
          // real geometry (overlaying the visible input above) so the
          // popper anchors correctly instead.
          <div
            style={{
              position: 'absolute',
              inset: 0,
              opacity: 0,
              pointerEvents: 'none',
            }}
          />
        }
        isClearable={false}
        calendarClassName={selectsRange ? '' : mtc(datePickerStyles())}
        renderCustomHeader={
          !selectsRange
            ? ({ ...props }) => (
                <DatePickerHeader
                  {...props}
                  showYear={showYear}
                  showMonthYearPicker={showMonthYearPicker}
                  showYearPicker={showYearPicker}
                  toggleShowYear={() => {
                    setShowYear((prev) => !prev)
                    // If you change the year pages the month pages also change even if a year is not selected and
                    // there's also no way to set the open date.  This is a hack to set the month back to whatever is selected
                    // It does end up flashing but it's better than the other behavior
                    pickerRef.current?.setOpen(false, true)
                    // There is a delay here because if the modal is set to closed -> open without this the modal doesn't actually close
                    setTimeout(() => pickerRef.current?.setOpen(true, true), 0)
                  }}
                />
              )
            : undefined
        }
        onSelect={() => setShowYear(Boolean(showYearPicker))}
        showMonthYearPicker={showMonthYearPicker}
        showYearPicker={showYear && !selectsRange}
        popperModifiers={popoverModifiers}
        popperContainer={popperContainer}
        calendarContainer={
          selectsRange
            ? ({ children }) => (
                <div
                  className={mtc(datePickerStyles())}
                  onMouseDown={(e) => e.stopPropagation()}
                  role="presentation"
                  style={{ backgroundColor: 'white' }}
                >
                  <div
                    className={mtc(leftPanelStyles())}
                    onMouseDown={(e) => e.stopPropagation()}
                    role="presentation"
                  >
                    <Label title="Filter by year">
                      <Select<number>
                        placeholder={
                          isMobileOrTablet ? 'Select' : 'Select year'
                        }
                        options={YEARLY_PNL_OPTIONS.map((option, index) => ({
                          text: String(option.value),
                          value: index,
                        }))}
                        onValueChange={(index) => {
                          handleQuickOption(YEARLY_PNL_OPTIONS[index])
                        }}
                        value={selectedYearlyOption}
                      />
                    </Label>

                    <Label title="Filter by quarter">
                      <Select<number>
                        placeholder={
                          isMobileOrTablet ? 'Select' : 'Select quarter'
                        }
                        options={QUARTER_PNL_OPTIONS.map((option, index) => ({
                          text: String(option.value),
                          value: index,
                        }))}
                        onValueChange={(index) => {
                          handleQuickOption(QUARTER_PNL_OPTIONS[index])
                        }}
                        value={selectedQuarterOption}
                      />
                    </Label>
                  </div>

                  <div className={mtc(calendarSectionStyles())}>
                    <div className={mtc(monthContainerStyles())}>
                      {children}
                    </div>

                    <div className={mtc(footerStyles())}>
                      <Button
                        className={mtc(cancelButtonStyles())}
                        onClick={handleCancel}
                        size="sm"
                        variant="ghost"
                      >
                        Cancel
                      </Button>

                      <Button
                        onClick={() => {
                          ;(onChange as (value: [string, string]) => void)([
                            start
                              ? convertSelectedDateToString(start, dateFormat)
                              : '',
                            end
                              ? convertSelectedDateToString(end, dateFormat)
                              : '',
                          ])
                          pickerRef.current?.setOpen(false)
                        }}
                        size="sm"
                        variant="primary"
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </div>
              )
            : undefined
        }
      />
    </div>
  )
}

export default DatePicker
export { type IDatePickerProps as DatePickerProps }
